import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import DefaultLayout from '../layouts/DefaultLayout';
import Project from '../interfaces/Project';
import { fetchWithAuth } from '../services/Fetch';
import { toast } from 'react-toastify';
import Customer from '../interfaces/Customer';

const ProjectsForm: React.FC = () => {
    const [searchParams] = useSearchParams();
    const { id } = useParams<{ id: string }>();
    const defaultCustomer = searchParams.get("customer") ? {id: parseInt(searchParams.get("customer") ?? "")} : null;
    const [project, setProject] = useState<Project>({ name: '', url: '', customer: defaultCustomer } as Project);    
    const [customers, setCustomers] = useState<Customer[]>([]);
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(true);

    const fetchCustomers = async () => {
        try {
            const response = await fetchWithAuth('/customers');
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setCustomers(data.member as Customer[]);
        } catch (error) {
            console.error('Error fetching customers:', error);
        }
    };

    const fetchProject = async () => {
        if (id) {
            try {
                const response = await fetchWithAuth('/projects/' + id);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                setProject(data as Project);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching project:', error);
                setLoading(false);

            }
        } else {
            setLoading(false);
        }

    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setProject(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = event.target;
        const selectedCustomer = customers.find(customer => customer.id === parseInt(value)) || null;
        setProject(prevState => ({
            ...prevState,
            [name]: selectedCustomer
        }));
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const payload = {
            name: project.name,
            url: project.url,
            customer: project.customer ? "/api/customers/" + project.customer.id : null
        };

        const response = await fetchWithAuth(
            '/projects' + (id ? "/" + id : ""),
            {
                method: id ? "PUT" : "POST",
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            }
        );
        if (!response.ok) {
            throw new Error('Network response was not ok');
        } else {
            toast.success("Projet ajouté")
            navigate("/projects");
        }
    };

    useEffect(() => {
        fetchProject();
        fetchCustomers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return <DefaultLayout><h1>Chargement...</h1></DefaultLayout>;
    }

    return (
        <DefaultLayout>
            <h1>Création d&apos;un projet</h1>
            <form onSubmit={handleSubmit}>
                <div className="mb-3">
                    <label htmlFor="name" className="form-label required-field">Titre</label>
                    <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={project.name}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="customer" className="form-label required-field">Client</label>
                    <select
                        className="form-control"
                        id="customer"
                        name="customer"
                        value={project.customer?.id ?? ''}
                        onChange={handleSelectChange}
                        required
                    >
                        <option value="" disabled>-- Sélectionner un client --</option>
                        {customers.map((customer) => (
                            <option key={customer.id} value={customer.id}>
                                {customer.title}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="mb-3">
                    <label htmlFor="url" className="form-label required-field">URL</label>
                    <input
                        type="url"
                        className="form-control"
                        id="url"
                        name="url"
                        value={project.url}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div className="row">
                    <div className="col-6"><button type="submit" className="btn btn-primary">{id ? "Modifier le projet" : "Créer le projet"}</button></div>
                    <div className="col-6 d-flex justify-content-end"><Link to="/projects" className="btn btn-danger">Annuler</Link></div>
                </div>
            </form>
        </DefaultLayout>
    );
}

export default ProjectsForm;